import type { Club, Match } from '@/types';
import { normalizeFavoriteIds, type FavoritesData } from '@/utils/favoritesJson';

/** Clé connexion GoPaaS du club (pas `idclub`). */
export function clubConnexionCle(club: Pick<Club, 'cle' | 'idclub'> | null | undefined): string {
  return String(club?.cle ?? '').trim();
}

/** Clé connexion GoPaaS du match (pas `idmatch_football`). */
export function matchConnexionCle(match: Pick<Match, 'cle' | 'idmatch_football'> | null | undefined): string {
  return String(match?.cle ?? '').trim();
}

function buildClubCleLookup(clubs: Club[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const club of clubs) {
    const cle = clubConnexionCle(club);
    if (!cle) continue;
    map.set(cle, cle);
    const id = String(club.idclub ?? '').trim();
    if (id) map.set(id, cle);
  }
  return map;
}

function buildMatchCleLookup(matches: Match[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const match of matches) {
    const cle = matchConnexionCle(match);
    if (!cle) continue;
    map.set(cle, cle);
    const id = String(match.idmatch_football ?? '').trim();
    if (id) map.set(id, cle);
  }
  return map;
}

/** Convertit les favoris locaux (legacy id ou cle) vers les `cle` connexion GoPaaS. */
export function normalizeFavoritesToConnexionCles(
  data: FavoritesData,
  clubs: Club[],
  matches: Match[],
): FavoritesData {
  const clubLookup = buildClubCleLookup(clubs);
  const matchLookup = buildMatchCleLookup(matches);

  const clubsOut = normalizeFavoriteIds(
    data.clubs.map((stored) => clubLookup.get(stored.trim()) ?? stored.trim()).filter(Boolean),
  );
  const matchesOut = normalizeFavoriteIds(
    data.matches.map((stored) => matchLookup.get(stored.trim()) ?? stored.trim()).filter(Boolean),
  );

  return { clubs: clubsOut, matches: matchesOut };
}

export function isClubFavoriteKey(storedClubs: string[], key: string, clubs: Club[]): boolean {
  const k = String(key || '').trim();
  if (!k) return false;
  if (storedClubs.includes(k)) return true;
  const lookup = buildClubCleLookup(clubs);
  const cle = lookup.get(k);
  return Boolean(cle && storedClubs.includes(cle));
}

export function isMatchFavoriteKey(storedMatches: string[], key: string, matches: Match[]): boolean {
  const k = String(key || '').trim();
  if (!k) return false;
  if (storedMatches.includes(k)) return true;
  const lookup = buildMatchCleLookup(matches);
  const cle = lookup.get(k);
  return Boolean(cle && storedMatches.includes(cle));
}
